export type UserRole = "affiliate" | "administrator" | "system_admin"

type StoredUser = {
  id: string
  fullName: string
  email: string
  dniOrSsn: string
  password?: string
  role: UserRole
  key?: string
}

type SessionUser = Omit<StoredUser, "password" | "key">

type LoginPayload = {
  email: string
  password: string
}

type RegisterPayload = {
  fullName: string
  email: string
  dniOrSsn: string
  password: string
}

type ServiceResult<T> = {
  ok: boolean
  error?: string
  user?: T
}

const SESSION_STORAGE_KEY = "idbp.session"
import { API_BASE_URL } from "../config"
const API_URL = API_BASE_URL

function canUseStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined"
}

function writeSession(user: StoredUser | null) {
  if (!canUseStorage()) {
    return
  }

  if (!user) {
    window.localStorage.removeItem(SESSION_STORAGE_KEY)
    return
  }

  const sessionUser: SessionUser = {
    id: user.id,
    fullName: user.fullName,
    email: user.email,
    dniOrSsn: user.dniOrSsn,
    role: user.role,
  }

  // Also store the token if it exists so we can use it for requests later
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
    ...sessionUser,
    key: user.key
  }))
}

export async function registerUser(payload: RegisterPayload): Promise<ServiceResult<SessionUser>> {
  try {
    const response = await fetch(`${API_URL}/auth/registration/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit", // Enforces statelessness by ignoring cookies
      body: JSON.stringify({
        email: payload.email,
        password1: payload.password,
        password2: payload.password,
        fullName: payload.fullName,
        dniOrSsn: payload.dniOrSsn,
      }),
    })

    const data = await response.json()
    if (!response.ok) {
      const errorMsg = data.non_field_errors?.[0] || data.email?.[0] || data.password1?.[0] || "El registro falló"
      return { ok: false, error: errorMsg }
    }

    // Auto login after registration
    return await loginUser({ email: payload.email, password: payload.password })
  } catch (error) {
    console.error("registerUser error:", error)
    return { ok: false, error: "Error de red" }
  }
}

export async function loginUser(payload: LoginPayload): Promise<ServiceResult<SessionUser>> {
  try {
    const response = await fetch(`${API_URL}/auth/login/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit", // Enforces statelessness by ignoring cookies
      body: JSON.stringify({
        email: payload.email,
        password: payload.password,
      }),
    })

    const data = await response.json()
    if (!response.ok) {
      return { ok: false, error: data.non_field_errors?.[0] || "Correo electrónico o contraseña inválidos." }
    }

    const key = data.key

    // Fetch full user details from /auth/user/
    const userRes = await fetch(`${API_URL}/auth/user/`, {
      method: "GET",
      headers: {
        "Authorization": `Token ${key}`,
        "Content-Type": "application/json",
      },
      credentials: "omit", // Enforces statelessness
    })

    if (!userRes.ok) {
      return { ok: false, error: "No se pudieron obtener los detalles del usuario." }
    }

    const userData = await userRes.json()

    const storedUser: StoredUser = {
      id: String(userData.id),
      fullName: userData.fullName || "",
      email: userData.email,
      dniOrSsn: userData.dniOrSsn || "",
      role: userData.role as UserRole,
      key: key,
    }

    writeSession(storedUser)

    const sessionUser: SessionUser = {
      id: storedUser.id,
      fullName: storedUser.fullName,
      email: storedUser.email,
      dniOrSsn: storedUser.dniOrSsn,
      role: storedUser.role,
    }

    return { ok: true, user: sessionUser }
  } catch (error) {
    console.error("loginUser error:", error)
    return { ok: false, error: "Error de red" }
  }
}

//  logout to destroy the token in Django
export async function logoutUser(): Promise<void> {
  const token = getToken()

  if (token) {
    try {
      await fetch(`${API_URL}/auth/logout/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${token}`,
        },
        credentials: "omit",
      })
    } catch (error) {
      console.error("Error connecting to backend during logout:", error)
    }
  }

  // Always clear local storage, even if the backend request fails
  clearSession()
}

export function getSession(): SessionUser | null {
  if (!canUseStorage()) {
    return null
  }

  const raw = window.localStorage.getItem(SESSION_STORAGE_KEY)
  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw)
    return {
      id: parsed.id,
      fullName: parsed.fullName,
      email: parsed.email,
      dniOrSsn: parsed.dniOrSsn,
      role: parsed.role,
    }
  } catch {
    return null
  }
}

export function getToken(): string | null {
  if (!canUseStorage()) return null
  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed.key || null
  } catch {
    return null
  }
}

export function clearSession() {
  writeSession(null)
}